import { FormEvent, useEffect, useState } from "react";
import { api, ActivationKey, Device, Plan, session } from "./api";
import { can } from "./can";
import { DeviceMosaic } from "./DeviceMosaic";
import { bytes, lifecycleLabel, percent } from "./format";
import { FilterBar, Metric, PageHeader } from "./ui";

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [layout, setLayout] = useState<"mosaic" | "table">("mosaic");
  const [filter, setFilter] = useState("");
  const [connection, setConnection] = useState("");
  const [tag, setTag] = useState("");
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const canManageKeys = can(session.user(), "key.manage");
  const visible = devices.filter(d => d.machineName.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) && (!connection || d.status === connection));
  const refresh = () =>
    api
      .devices(tag || undefined)
      .then(setDevices)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void refresh();
  }, [tag]);
  useEffect(() => {
    api.deviceTags().then((result) => setTagOptions(result.tags)).catch(() => undefined);
  }, []);
  const online = devices.filter((d) => d.status === "online").length;
  return (
    <>
      <PageHeader
        eyebrow="VISÃO GERAL"
        title="Equipamentos"
        subtitle="Estado atual da sua infraestrutura monitorada."
        actions={
          <>
            <div className="live">
              <i /> Dados do último contato
            </div>
            {canManageKeys && (
              <button onClick={() => setShowAdd(true)}>
                + Adicionar equipamento
              </button>
            )}
          </>
        }
      />
      {error && <p className="error">{error}</p>}
      <section className="stats">
        <Metric label="Total" value={devices.length} />
        <Metric label="Online" value={online} tone="green" />
        <Metric label="Offline" value={devices.length - online} tone="orange" />
      </section>
      <FilterBar
        actions={
          <>
            <button type="button" className="secondary" onClick={() => setLayout(layout === "mosaic" ? "table" : "mosaic")}>{layout === "mosaic" ? "Ver tabela" : "Ver mosaico"}</button>
            <button type="button" className="secondary" onClick={() => void refresh()}>Atualizar</button>
          </>
        }
      >
        <label>Equipamento<input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Buscar pelo nome" /></label>
        <label>Conexão<select value={connection} onChange={e => setConnection(e.target.value)}><option value="">Todas</option><option value="online">Online</option><option value="offline">Offline</option></select></label>
        <label>Etiqueta<select value={tag} onChange={e => setTag(e.target.value)}><option value="">Todas</option>{tagOptions.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
      </FilterBar>
      <section className="panel">
        <div className="panel-title">
          <h2>Dispositivos</h2>
          <span>{visible.length} de {devices.length} registrados</span>
        </div>
        {layout === "mosaic" ? <DeviceMosaic devices={visible} /> : <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Equipamento</th>
                <th>Etiquetas</th>
                <th>Conexão</th>
                <th>Controle</th>
                <th>Plano</th>
                <th>CPU</th>
                <th>Memória</th>
                <th>Disco livre</th>
                <th>Último contato</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => (location.hash = `#/devices/${d.id}`)}
                >
                  <td>
                    <strong>{d.machineName}</strong>
                    <small>{d.osVersion}</small>
                  </td>
                  <td>
                    {d.tags?.length
                      ? d.tags.map((t) => <span className="tag-chip" key={t}>{t}</span>)
                      : <small>—</small>}
                  </td>
                  <td>
                    <span className={`status ${d.status}`}>{d.status}</span>
                  </td>
                  <td>
                    <span className={`status lifecycle-${d.lifecycleState}`}>
                      {lifecycleLabel(d.lifecycleState)}
                    </span>
                  </td>
                  <td><strong>{d.planName}</strong><small>{d.screenRetentionDays} dias de telas</small></td>
                  <td>{percent(d.cpuPercent)}</td>
                  <td>{percent(d.memoryUsedPercent)}</td>
                  <td>{bytes(d.diskFreeBytes)}</td>
                  <td>
                    {d.lastSeenAt
                      ? new Date(d.lastSeenAt).toLocaleString("pt-BR")
                      : "Nunca"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {devices.length === 0 && (
            <div className="empty">
              <b>Nenhum equipamento ativado</b>
              <span>
                Use “Adicionar equipamento” para gerar uma chave segura.
              </span>
            </div>
          )}
        </div>}
      </section>
      {showAdd && <AddDeviceModal onClose={() => setShowAdd(false)} />}
    </>
  );
}

function AddDeviceModal({ onClose }: { onClose: () => void }) {
  const [label, setLabel] = useState("Novo equipamento");
  const [key, setKey] = useState("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [keys, setKeys] = useState<ActivationKey[]>([]);
  const [planId, setPlanId] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [generatedExpiresAt, setGeneratedExpiresAt] = useState("");
  const [error, setError] = useState("");
  const refreshKeys = () => api.activationKeys().then(setKeys).catch((e) => setError(e.message));
  useEffect(() => {
    api.plans().then((items) => { setPlans(items); setPlanId(items[0]?.id ?? ""); }).catch((e) => setError(e.message));
    void refreshKeys();
  }, []);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const created = await api.createActivationKey(label, maxUses, planId);
      setKey(created.key ?? "");
      setGeneratedExpiresAt(created.expiresAt);
      await refreshKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao criar chave.");
    }
  };
  const copy = () => navigator.clipboard.writeText(key);
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">ATIVAÇÃO DO AGENTE</p>
        <h2>Adicionar equipamento</h2>
        <p className="muted">
          Instale o agente no computador e informe a chave de ativação para conectá-lo.
        </p>
        <a
          className="button-link agent-download"
          href="/downloads/DBGuardian-Agent-Setup-latest.exe"
        >
          Baixar instalador do agente (Windows) · v{__APP_VERSION__}
        </a>
        <p className="muted">
          Implantação em massa (GPO/silencioso)?{" "}
          <a href="/downloads/DBGuardian-Agent-latest-win-x64.zip">Baixe o pacote .zip</a>.
        </p>
        {key ? (
          <>
            <p className="muted">
              Esta chave é exibida uma única vez, permite {maxUses} ativação{maxUses === 1 ? "" : "ões"} e expira em {new Date(generatedExpiresAt).toLocaleString("pt-BR")}.
            </p>
            <code className="activation-key">{key}</code>
            <button onClick={copy}>Copiar chave</button>
            <button className="secondary" onClick={onClose}>
              Concluir
            </button>
          </>
        ) : (
          <form onSubmit={create}>
            <label>
              Identificação
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </label>
            <label>Plano<select value={planId} onChange={(e) => setPlanId(e.target.value)} required>{plans.map((plan) => <option value={plan.id} key={plan.id}>{plan.name} · {plan.screenRetentionDays} dias de telas</option>)}</select></label>
            <label>Quantidade de instalações<input type="number" min="1" max="10000" value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} required /></label>
            {maxUses > 1 && <p className="muted">Chave para implantação em massa. Cada computador receberá sua própria credencial.</p>}
            {error && <p className="error">{error}</p>}
            <button>Gerar chave de ativação</button>
          </form>
        )}
        {keys.length > 0 && <div className="activation-list"><h3>Chaves recentes</h3>{keys.slice(0, 8).map((item) => {
          const active = !item.revokedAt && new Date(item.expiresAt) > new Date() && item.usedCount < item.maxUses;
          return <div key={item.id}><span><strong>{item.label || "Sem identificação"}</strong><small>{item.planName} · {item.usedCount}/{item.maxUses} usos · até {new Date(item.expiresAt).toLocaleString("pt-BR")}</small></span>{active && <button className="small-button danger" onClick={async () => { await api.revokeActivationKey(item.id); await refreshKeys(); }}>Revogar</button>}</div>;
        })}</div>}
      </section>
    </div>
  );
}
